/**
 * Member Guests tab (DNKADM-0068 U1) — the history list of everyone a member has
 * invited as a guest, filling a new tab slot on `MemberDetail`. Backed by
 * `getMemberGuests(contactId, status)` on the bearer-only `/v2/admin/**` surface,
 * loaded through `useResource` (keyed on the status filter), mirroring the other
 * member-detail tab sections.
 *
 * One row per guest: the display name (`firstName lastName` → `email` → "Guest
 * {guestUserId}"), the email, a status chip (Active / Archived / Blocked — distinct
 * colors), the invite counts ("invited N×" lifetime + "M this month"), and the
 * humanized first/last-invited instants. A status filter (Active / Archived /
 * Blocked / All — default **All**, since admin sees every status) drives the
 * `status` query. Loading / empty / error+Retry states.
 *
 * The per-row moderation BUTTONS render here, gated to each row's status (ACTIVE →
 * Archive + Block; ARCHIVED → Unarchive + Block; BLOCKED → Unblock), each opening the
 * **U2** confirm dialog (ARCHIVE soft-hide vs BLOCK hard-stop — distinct copy) →
 * `*MemberGuest` call → update-in-place + toast + refetch. Block and Unblock follow
 * `canBlockGuests` (DNKADM-0465, DNKAPI-0675): ADMIN only on the server, so a STAFF session
 * archives and unarchives but sees neither (the read fails closed for STAFF). A STAFF
 * archive of a member homed at another location is a `403`, shown like any refusal.
 *
 * Every row also offers a **View usage** drill-down (**U3**): a compact read-only
 * dialog over `getGuestUsage(guestUserId)` showing how heavily this guest is invited
 * across the whole tenant — `totalInvitesAllMembers`, `distinctMembers`, and the
 * per-member breakdown (each member's name → `memberContactId` fallback, invite count,
 * and that member's moderation status for this guest, with a link to the member when
 * resolvable). It is not a route.
 *
 * White-label (§4.0): backend-sourced text (guest + member names, email) is scrubbed
 * at the render boundary via `scrubBrand`. Bearer-only `/v2/admin/**`.
 *
 * DNKADM-0430 P1-D: the status toggle and a `SortControl` (Last invited, Name, Status,
 * Times invited, First invited) sit in the shared list toolbar, and both live in the URL
 * (`guestStatus`, `guestSort`/`guestDir`, see `memberDetailView.ts`). The status is still
 * a server filter; the sort is client side over the whole returned list. The default
 * sort is Last invited, newest first: the server's own order.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { MemberNameLink } from '@/components/MemberNameLink';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Skeleton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Icon, inkAlpha, resolveTokens } from '@/theme';
import { buildMembersMaps, type MembersMaps } from '@/theme/maps/members';
import { ApiError } from '@/api/errors';
import { useResource } from '@/hooks/useResource';
import { useToast } from '@/components/useToast';
import { useMoneyPermissions } from '@/auth/moneyPermissions';
import { ListToolbar } from '@/components/list/ListToolbar';
import { parseSort, sortRows, toSortParams } from '@/components/list/listSort';
import { SortControl } from '@/components/list/SortControl';
import { parseOption, useListUrlState } from '@/components/list/useListUrlState';
import { scrubBrand } from '@/utils/whitelabel';
import { formatRelativeTime } from '@/utils/relativeTime';
import {
  archiveMemberGuest,
  blockMemberGuest,
  getGuestUsage,
  getMemberGuests,
  unarchiveMemberGuest,
  unblockMemberGuest,
  type GuestUsage,
  type GuestUsagePerMember,
  type MemberGuest,
  type MemberGuestStatus,
  type MemberGuestStatusFilter,
} from '@/api/memberGuests';
import {
  MEMBERS_FORBIDDEN_MESSAGE,
  MEMBERS_LOAD_FAILED_MESSAGE,
  toGuestModerationErrorMessage,
} from './membersError';
import {
  GUEST_DEFAULT_SORT,
  GUEST_SORT_KEYS,
  GUEST_SORT_OPTIONS,
  GUEST_STATUS_FILTERS,
  GUESTS_URL_DEFAULTS,
  guestSortValue,
} from './memberDetailView';
import { ScreenError } from '@/components/ScreenError';

const SKELETON_ROWS = 4;

/** A moderation verb available on a row (the buttons rendered per status). */
export type GuestModerationAction = 'archive' | 'unarchive' | 'block' | 'unblock';

/**
 * Status-chip presentation — distinct colors per the three states (DNKAPI-0147).
 * Mode-aware since DNKADM-0269 (the pairs live in `theme/maps/members`).
 */
function statusChip(
  members: MembersMaps,
): Record<MemberGuestStatus, { label: string; fg: string; bg: string }> {
  return {
    ACTIVE: { label: 'Active', ...members.guestStatus.active },
    ARCHIVED: { label: 'Archived', ...members.guestStatus.archived },
    BLOCKED: { label: 'Blocked', ...members.guestStatus.blocked },
  };
}

/** The status-filter options, in display order — default selection is `all`. */
const FILTER_OPTIONS: ReadonlyArray<{ value: MemberGuestStatusFilter; label: string }> = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'ARCHIVED', label: 'Archived' },
  { value: 'BLOCKED', label: 'Blocked' },
  { value: 'all', label: 'All' },
];

/**
 * The moderation buttons a row offers, gated to its current status (DNKAPI-0147):
 *  - ACTIVE → Archive + Block; ARCHIVED → Unarchive (+ still Block); BLOCKED →
 *    Unblock. An unknown/`null` status offers none (a half-identified row isn't
 *    actioned). U2 wires each to its confirm dialog.
 *  - Without `canBlock` (STAFF, DNKAPI-0675) Block and Unblock are dropped, so a BLOCKED
 *    row offers none.
 */
function actionsForStatus(
  status: MemberGuestStatus | null,
  canBlock: boolean,
): ReadonlyArray<{ action: GuestModerationAction; label: string; icon: string }> {
  const block = { action: 'block', label: 'Block', icon: 'block' } as const;
  switch (status) {
    case 'ACTIVE':
      return [
        { action: 'archive', label: 'Archive', icon: 'archive' },
        ...(canBlock ? [block] : []),
      ];
    case 'ARCHIVED':
      return [
        { action: 'unarchive', label: 'Unarchive', icon: 'unarchive' },
        ...(canBlock ? [block] : []),
      ];
    case 'BLOCKED':
      return canBlock ? [{ action: 'unblock', label: 'Unblock', icon: 'check_circle' }] : [];
    default:
      return [];
  }
}

/** The `*MemberGuest` call backing each moderation verb (each returns the updated row). */
const MODERATION_CALL: Record<
  GuestModerationAction,
  (memberGuestId: string) => Promise<MemberGuest | null>
> = {
  archive: archiveMemberGuest,
  unarchive: unarchiveMemberGuest,
  block: blockMemberGuest,
  unblock: unblockMemberGuest,
};

/** Success toast per verb (server-truthful: raised only after the call resolves). */
const MODERATION_SUCCESS: Record<GuestModerationAction, string> = {
  archive: 'Guest archived.',
  unarchive: 'Guest unarchived.',
  block: 'Guest blocked.',
  unblock: 'Guest unblocked.',
};

/**
 * The confirm-dialog copy per verb. The ARCHIVE vs BLOCK wording is **deliberately
 * distinct** (DNKAPI-0147): ARCHIVE = a soft hide that auto-reactivates when the
 * member re-invites the guest; BLOCK = a hard stop only an admin can lift. Each verb
 * carries its own body — never one generic "are you sure."
 */
const MODERATION_DIALOG: Record<
  GuestModerationAction,
  { title: string; icon: string; body: string; confirmLabel: string; busyLabel: string; danger: boolean }
> = {
  archive: {
    title: 'Archive guest',
    icon: 'archive',
    body:
      'Hides this guest from the member’s pick list. If the member invites them again, ' +
      'they come back automatically.',
    confirmLabel: 'Archive',
    busyLabel: 'Archiving…',
    danger: false,
  },
  unarchive: {
    title: 'Unarchive guest',
    icon: 'unarchive',
    body:
      'Restores this guest to the member’s pick list. They show up again the next time ' +
      'this member adds guests.',
    confirmLabel: 'Unarchive',
    busyLabel: 'Unarchiving…',
    danger: false,
  },
  block: {
    title: 'Block guest',
    icon: 'block',
    body:
      'This member won’t be able to add this guest to a reservation until you unblock ' +
      'them. Guests already on existing reservations aren’t removed.',
    confirmLabel: 'Block',
    busyLabel: 'Blocking…',
    danger: true,
  },
  unblock: {
    title: 'Unblock guest',
    icon: 'check_circle',
    body: 'This member will be able to add this guest again.',
    confirmLabel: 'Unblock',
    busyLabel: 'Unblocking…',
    danger: false,
  },
};

/** Best display name: `firstName lastName` → `email` → "Guest {guestUserId}". */
function guestName(guest: MemberGuest): string {
  const full = [guest.firstName, guest.lastName]
    .filter((s): s is string => typeof s === 'string' && s.length > 0)
    .join(' ');
  const name = full.length > 0 ? full : (guest.email ?? `Guest ${guest.guestUserId}`);
  return scrubBrand(name) ?? name;
}

export function MemberGuestsTab({ contactId }: { contactId: string }) {
  const showToast = useToast();
  const { canBlockGuests } = useMoneyPermissions();
  // The row currently in the moderation confirm dialog (null = no dialog).
  const [pending, setPending] = useState<{ guest: MemberGuest; action: GuestModerationAction } | null>(
    null,
  );
  // The guest whose cross-member usage drill-down is open (U3), or null when closed.
  const [usageGuest, setUsageGuest] = useState<MemberGuest | null>(null);
  // Returned rows applied IN PLACE so a chip/actions update immediately after a
  // moderation call, before the refetch lands. Cleared when fresh server data
  // arrives (the server is then authoritative), keyed by `memberGuestId`.
  const [overrides, setOverrides] = useState<ReadonlyMap<string, MemberGuest>>(new Map());
  const { values, set: setUrl } = useListUrlState(GUESTS_URL_DEFAULTS);
  const filter = parseOption<MemberGuestStatusFilter>(
    values.guestStatus,
    GUEST_STATUS_FILTERS,
    'all',
  );
  const sort = parseSort(values.guestSort, values.guestDir, GUEST_SORT_KEYS, GUEST_DEFAULT_SORT);

  const guests = useResource(
    useCallback(() => getMemberGuests(contactId, filter), [contactId, filter]),
    [contactId, filter],
  );
  const { refetch } = guests;

  // Once the server returns a fresh list (initial load, filter change, or a
  // post-moderation refetch), the in-place overrides are redundant — drop them so a
  // stale override can never shadow server truth.
  useEffect(() => {
    setOverrides((prev) => (prev.size === 0 ? prev : new Map()));
  }, [guests.data]);

  // Open the confirm dialog for a row + verb. The call/toast/refetch run on confirm.
  const handleModerate = useCallback((guest: MemberGuest, action: GuestModerationAction) => {
    setPending({ guest, action });
  }, []);

  // Open the cross-member usage drill-down for a row (U3) — read-only, loads on open.
  const handleViewUsage = useCallback((guest: MemberGuest) => {
    setUsageGuest(guest);
  }, []);

  // A moderation call resolved: reflect the updated row in place, toast, refetch
  // (respecting the active filter), and close the dialog.
  const handleModerated = useCallback(
    (action: GuestModerationAction, updated: MemberGuest | null) => {
      if (updated) {
        setOverrides((prev) => new Map(prev).set(updated.memberGuestId, updated));
      }
      showToast(MODERATION_SUCCESS[action]);
      setPending(null);
      refetch();
    },
    [showToast, refetch],
  );

  // The relationship wasn't found for this tenant (a stale/foreign id) — the list is
  // out of date, so toast + refetch rather than surface an inline error.
  const handleNotFound = useCallback(() => {
    showToast('That guest record was not found — refreshing.', 'error');
    setPending(null);
    refetch();
  }, [showToast, refetch]);

  // Apply any in-place override so a just-moderated row shows its new status while
  // the refetch is in flight.
  // Equal values keep the server's order (`lastInvitedAt DESC`), so no tiebreak key.
  const rows = sortRows(
    (guests.data ?? []).map((g) => overrides.get(g.memberGuestId) ?? g),
    (guest) => guestSortValue(guest, sort.key, guestName(guest)),
    sort.dir,
  );
  // Skeletons only on the FIRST load; a refetch keeps the previous rows visible so the
  // in-place status update isn't hidden behind a flash of skeletons.
  const firstLoading = guests.status === 'loading' && guests.data === null;

  return (
    <Box>
      <ListToolbar
        filters={
          <>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={filter}
              onChange={(_e, next: MemberGuestStatusFilter | null) => {
                // `exclusive` emits null when the active button is re-clicked; ignore it
                // so the filter always has a value.
                if (next !== null) setUrl({ guestStatus: next });
              }}
              aria-label="Filter guests by status"
            >
              {FILTER_OPTIONS.map((opt) => (
                <ToggleButton
                  key={opt.value}
                  value={opt.value}
                  sx={{ textTransform: 'none', px: 1.25, py: 0.25 }}
                >
                  {opt.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <SortControl
              sort={sort}
              options={GUEST_SORT_OPTIONS}
              onChange={(next) => {
                const params = toSortParams(next, GUEST_DEFAULT_SORT);
                setUrl({ guestSort: params.sort, guestDir: params.dir });
              }}
            />
          </>
        }
      />

      {firstLoading && (
        <Paper elevation={1} sx={{ px: 3, py: 1 }} role="status" aria-label="Loading guests">
          {Array.from({ length: SKELETON_ROWS }).map((_, i) => (
            <SkeletonRow key={`sk-${i}`} />
          ))}
        </Paper>
      )}

      {guests.status === 'error' && <GuestsError error={guests.error} onRetry={refetch} />}

      {!firstLoading && guests.status !== 'error' && rows.length === 0 && <EmptyState />}

      {!firstLoading && guests.status !== 'error' && rows.length > 0 && (
        <Paper elevation={1} sx={{ px: 3, py: 1 }}>
          {rows.map((guest, i) => (
            <GuestRow
              key={guest.memberGuestId}
              guest={guest}
              last={i === rows.length - 1}
              canBlock={canBlockGuests}
              onModerate={handleModerate}
              onViewUsage={handleViewUsage}
            />
          ))}
        </Paper>
      )}

      {pending && (
        <GuestModerationDialog
          key={`${pending.guest.memberGuestId}-${pending.action}`}
          guest={pending.guest}
          action={pending.action}
          onClose={() => setPending(null)}
          onModerated={(updated) => handleModerated(pending.action, updated)}
          onNotFound={handleNotFound}
        />
      )}

      {usageGuest && (
        <GuestUsageDialog
          key={usageGuest.guestUserId}
          guest={usageGuest}
          onClose={() => setUsageGuest(null)}
        />
      )}
    </Box>
  );
}

/**
 * The moderation confirm dialog (DNKADM-0068 U2). Shows the verb-specific copy
 * (ARCHIVE soft-hide vs BLOCK hard-stop — see {@link MODERATION_DIALOG}), runs the
 * matching `*MemberGuest` call on confirm, and reports the outcome up:
 *  - success → `onModerated(updatedRow)` (parent updates in place + toasts + refetches);
 *  - a `404` → `onNotFound()` (parent toasts + refetches — the list is stale);
 *  - any other error → an inline alert, the dialog stays open to retry.
 * A ref guard makes a double-click on Confirm fire the call exactly once.
 */
function GuestModerationDialog({
  guest,
  action,
  onClose,
  onModerated,
  onNotFound,
}: {
  guest: MemberGuest;
  action: GuestModerationAction;
  onClose: () => void;
  onModerated: (updated: MemberGuest | null) => void;
  onNotFound: () => void;
}) {
  const theme = useTheme();
  const t = resolveTokens(theme);
  const members = buildMembersMaps(theme.palette.mode === 'dark' ? 'dark' : 'light');
  const copy = MODERATION_DIALOG[action];
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Guards a synchronous double-click on Confirm: the disabled prop only takes effect
  // on the next render, so the ref blocks the second handler in the same tick.
  const inFlight = useRef(false);

  function close() {
    if (submitting) return;
    onClose();
  }

  async function handleConfirm() {
    if (inFlight.current) return;
    inFlight.current = true;
    setSubmitting(true);
    setError(null);
    try {
      const updated = await MODERATION_CALL[action](guest.memberGuestId);
      onModerated(updated);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'NOT_FOUND') {
        onNotFound();
        return;
      }
      setError(toGuestModerationErrorMessage(err));
      setSubmitting(false);
      inFlight.current = false;
    }
  }

  const tone = copy.danger ? members.dangerAlt.fg : t.brand;
  const toneBg = copy.danger ? members.dangerAlt.bg : t.brandSoft;

  return (
    <Dialog open onClose={close} maxWidth="xs" fullWidth aria-labelledby="guest-moderation-title">
      <DialogTitle
        id="guest-moderation-title"
        sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, pb: 0.5 }}
      >
        <Avatar variant="rounded" sx={{ bgcolor: toneBg, color: tone, width: 38, height: 38 }}>
          <Icon name={copy.icon} sx={{ fontSize: 21 }} />
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 500 }}>{copy.title}</Typography>
          <Typography variant="body2">{guestName(guest)}</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" sx={{ color: inkAlpha(theme, 0.7) }}>
          {copy.body}
        </Typography>
        {error && (
          <Alert severity="error" role="alert" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ borderTop: `1px solid ${t.lineSoft}`, px: 2.5, py: 1.5 }}>
        <Button color="inherit" onClick={close} disabled={submitting} sx={{ color: t.text2 }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color={copy.danger ? 'error' : 'primary'}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <Icon name={copy.icon} />}
          onClick={() => void handleConfirm()}
          disabled={submitting}
        >
          {submitting ? copy.busyLabel : copy.confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * The cross-member usage drill-down (DNKADM-0068 U3) — a compact, read-only dialog
 * over `getGuestUsage(guestUserId)` showing how heavily this guest is invited across
 * the whole tenant: the headline totals (`totalInvitesAllMembers`, `distinctMembers`)
 * and the per-member breakdown (each member's name → `memberContactId` fallback,
 * invite count, and that member's moderation status for this guest, linked to the
 * member's detail when resolvable). Loads on open (`useResource`); loading / empty /
 * error+Retry states. It is not a route — a link navigates away and closes the dialog.
 */
function GuestUsageDialog({ guest, onClose }: { guest: MemberGuest; onClose: () => void }) {
  const theme = useTheme();
  const t = resolveTokens(theme);
  const members = buildMembersMaps(theme.palette.mode === 'dark' ? 'dark' : 'light');
  const usage = useResource(
    useCallback(() => getGuestUsage(guest.guestUserId), [guest.guestUserId]),
  );
  const name = guestName(guest);
  const loading = usage.status === 'loading' && usage.data === null;

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth aria-labelledby="guest-usage-title">
      <DialogTitle
        id="guest-usage-title"
        sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, pb: 0.5 }}
      >
        <Avatar
          variant="rounded"
          sx={{ bgcolor: members.guestAvatar.bg, color: members.guestAvatar.fg, width: 38, height: 38 }}
        >
          <Icon name="insights" sx={{ fontSize: 21 }} />
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 500 }}>Guest usage across members</Typography>
          <Typography variant="body2">{name}</Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading && (
          <Stack sx={{ gap: 1 }} role="status" aria-label="Loading usage">
            <Skeleton variant="rounded" height={52} />
            <Skeleton variant="rounded" height={40} sx={{ mt: 1 }} />
            <Skeleton variant="rounded" height={40} />
          </Stack>
        )}

        {usage.status === 'error' && <GuestsError error={usage.error} onRetry={usage.refetch} />}

        {usage.status === 'ready' && usage.data !== null && (
          <GuestUsageBody usage={usage.data} onNavigate={onClose} />
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.5 }}>
        <Button color="inherit" onClick={onClose} sx={{ color: t.text2 }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** The loaded usage body: the headline totals + the per-member breakdown. */
function GuestUsageBody({ usage, onNavigate }: { usage: GuestUsage; onNavigate: () => void }) {
  const theme = useTheme();
  const t = resolveTokens(theme);
  return (
    <>
      <Stack direction="row" sx={{ gap: 1.5, mb: 2.5, flexWrap: 'wrap' }}>
        <UsageStat label="Total invites" value={usage.totalInvitesAllMembers} />
        <UsageStat label="Distinct members" value={usage.distinctMembers} />
      </Stack>

      <Typography sx={{ fontSize: 13, fontWeight: 500, color: t.text2, mb: 0.5 }}>
        By member
      </Typography>
      {usage.perMember.length === 0 ? (
        <Typography variant="body2" sx={{ color: t.text2, py: 1 }}>
          No members have invited this guest yet.
        </Typography>
      ) : (
        <Stack divider={<Divider flexItem />}>
          {usage.perMember.map((member) => (
            <UsagePerMemberRow key={member.memberContactId} member={member} onNavigate={onNavigate} />
          ))}
        </Stack>
      )}
    </>
  );
}

/** A headline figure (total invites / distinct members) in a tinted box. */
function UsageStat({ label, value }: { label: string; value: number }) {
  const theme = useTheme();
  const t = resolveTokens(theme);
  const members = buildMembersMaps(theme.palette.mode === 'dark' ? 'dark' : 'light');
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 130,
        px: 2,
        py: 1.25,
        borderRadius: 2,
        border: `1px solid ${inkAlpha(theme, 0.1)}`,
        bgcolor: members.surfaces.workspace,
      }}
    >
      <Typography sx={{ fontSize: 22, fontWeight: 600 }}>{value}</Typography>
      <Typography variant="body2" sx={{ color: t.text2 }}>
        {label}
      </Typography>
    </Box>
  );
}

/**
 * One per-member usage row: the member's name (→ `memberContactId` fallback) linked to
 * that member's detail (closing this dialog on navigate), their moderation status chip
 * for this guest, and the invite count. Backend text is brand-scrubbed (§4.0).
 */
function UsagePerMemberRow({
  member,
  onNavigate,
}: {
  member: GuestUsagePerMember;
  onNavigate: () => void;
}) {
  const theme = useTheme();
  const t = resolveTokens(theme);
  const members = buildMembersMaps(theme.palette.mode === 'dark' ? 'dark' : 'light');
  const name = scrubBrand(member.memberName) ?? `Member ${member.memberContactId}`;
  const chip = member.status !== null ? statusChip(members)[member.status] : null;

  return (
    <Stack
      direction="row"
      sx={{ py: 1.25, alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}
    >
      <Box sx={{ flex: 1, minWidth: 180 }}>
        <Stack direction="row" sx={{ alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography component="span" sx={{ fontSize: 14, fontWeight: 500 }}>
            <MemberNameLink
              name={name}
              contactId={member.memberContactId}
              onClick={onNavigate}
            />
          </Typography>
          {chip && (
            <Chip
              size="small"
              label={chip.label}
              sx={{ height: 20, fontSize: 11, color: chip.fg, bgcolor: chip.bg }}
            />
          )}
        </Stack>
      </Box>
      <Typography sx={{ fontSize: 12.5, color: t.text2 }}>
        invited {member.inviteCount}×
      </Typography>
    </Stack>
  );
}

function GuestRow({
  guest,
  last,
  canBlock,
  onModerate,
  onViewUsage,
}: {
  guest: MemberGuest;
  last: boolean;
  /** Block / Unblock (ADMIN only on the server, DNKAPI-0675). */
  canBlock: boolean;
  onModerate: (guest: MemberGuest, action: GuestModerationAction) => void;
  onViewUsage: (guest: MemberGuest) => void;
}) {
  const theme = useTheme();
  const t = resolveTokens(theme);
  const members = buildMembersMaps(theme.palette.mode === 'dark' ? 'dark' : 'light');
  const name = guestName(guest);
  const email = scrubBrand(guest.email);
  const chip = guest.status !== null ? statusChip(members)[guest.status] : null;
  const actions = actionsForStatus(guest.status, canBlock);

  const firstInvited = formatRelativeTime(guest.firstInvitedAt);
  const lastInvited = formatRelativeTime(guest.lastInvitedAt);

  return (
    <Stack
      direction="row"
      sx={{
        gap: 1.75,
        py: 1.5,
        alignItems: 'center',
        flexWrap: 'wrap',
        borderBottom: last ? 'none' : `1px solid ${inkAlpha(theme, 0.05)}`,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 220 }}>
        <Stack direction="row" sx={{ alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: 14, fontWeight: 500 }}>{name}</Typography>
          {chip && (
            <Chip
              size="small"
              label={chip.label}
              sx={{ height: 20, fontSize: 11, color: chip.fg, bgcolor: chip.bg }}
            />
          )}
        </Stack>
        {email !== null && email !== name && (
          <Typography variant="body2" sx={{ color: t.textSubtle, mt: 0.25 }}>
            {email}
          </Typography>
        )}
        <Stack
          direction="row"
          sx={{ gap: 1.5, flexWrap: 'wrap', mt: 0.5, color: t.text2, fontSize: 12.5 }}
        >
          <Box component="span">
            invited {guest.totalInviteCount}× · {guest.invitesThisMonth} this month
          </Box>
          {firstInvited !== null && <Box component="span">First {firstInvited}</Box>}
          {lastInvited !== null && <Box component="span">Last {lastInvited}</Box>}
        </Stack>
      </Box>

      <Stack direction="row" sx={{ gap: 1, flexShrink: 0, flexWrap: 'wrap' }}>
        <Button
          size="small"
          variant="outlined"
          color="inherit"
          startIcon={<Icon name="insights" sx={{ fontSize: 17 }} />}
          onClick={() => onViewUsage(guest)}
          sx={{ textTransform: 'none' }}
        >
          View usage
        </Button>
        {actions.map((a) => (
          <Button
            key={a.action}
            size="small"
            variant="outlined"
            color="inherit"
            startIcon={<Icon name={a.icon} sx={{ fontSize: 17 }} />}
            onClick={() => onModerate(guest, a.action)}
            sx={{ textTransform: 'none' }}
          >
            {a.label}
          </Button>
        ))}
      </Stack>
    </Stack>
  );
}

function SkeletonRow() {
  return (
    <Stack direction="row" sx={{ gap: 1.75, py: 1.875, alignItems: 'center' }}>
      <Box sx={{ flex: 1 }}>
        <Skeleton width={200} height={14} />
        <Skeleton width={260} height={12} sx={{ mt: 0.5 }} />
      </Box>
      <Skeleton variant="rounded" width={84} height={30} />
    </Stack>
  );
}

function EmptyState() {
  const theme = useTheme();
  return (
    <Box sx={{ py: 6, textAlign: 'center' }}>
      <Icon name="group" sx={{ fontSize: 42, color: inkAlpha(theme, 0.2) }} />
      <Typography sx={{ fontSize: 15, fontWeight: 500, mt: 1 }}>
        This member hasn’t invited any guests
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.5, maxWidth: 340, mx: 'auto' }}>
        Guests a member adds to their reservations will appear here, with how often
        they’ve been invited.
      </Typography>
    </Box>
  );
}

function GuestsError({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  return (
    <ScreenError
      error={error}
      what="the guests"
      onRetry={onRetry}
      forbiddenMessage={MEMBERS_FORBIDDEN_MESSAGE}
      retryMessage={MEMBERS_LOAD_FAILED_MESSAGE}
      preferServerMessage
      sx={{ alignItems: 'center' }}
    />
  );
}
